/*
 * Copyright (c) 2022 biocrates life sciences ag, All rights reserved.
 */

import {ColDef, FilterChangedEvent} from "ag-grid-community"
import {ValueGetterParams} from "ag-grid-community/dist/lib/entities/colDef"
import {SortChangedEvent} from "ag-grid-community/dist/lib/events"
import _ from "lodash"

import {ColumnWidth} from "../models/columnWidth"
import SortableTableData from "../models/sortableTableData"
import TableCell from "../models/tableCell"
import TableColumn from "../models/tableColumn"
import {TableDef} from "../models/tableDef"
import {TableFiltering, TableFilteringType} from "../models/tableFiltering"
import TableRow from "../models/tableRow"
import {TableSorting} from "../models/tableSorting"
import RawSortableTableData from "../models/rawSortableTableData"

export const defaultFilterSettings: ColDef = {
    filter: "agTextColumnFilter",
    filterParams: {
        defaultJoinOperator: "OR",
        buttons: ["apply", "reset"],
    },
}

export const getTableDefFromRawSortableTableData = (
    data: RawSortableTableData,
    numFrozenColumns = 0,
    disableInternalSorting = true
): TableDef => ({
    cols: data.columns.map((colDef: ColDef, index: number) => {
        const sortingIndex = data.sorting
            .filter((ts) =>
                data.columns.map((c) => c.field).includes(ts.columnId)
            )
            .findIndex((entry) => entry.columnId === colDef.field)
        return {
            ...colDef,
            pinned: index < numFrozenColumns ? "left" : null,
            sort: data.sorting[sortingIndex]?.order || null,
            sortIndex: data.sorting[sortingIndex] ? sortingIndex : null,
            ...(data.colWidths.find(
                (colWidth) => colDef.field && colWidth[colDef.field]
            ) && {
                width: (data.colWidths.find(
                    (colWidth) => colWidth[colDef.field as string]
                ) as ColumnWidth)[colDef.field as string],
            }),
            ...(disableInternalSorting
                ? {comparator: () => 0}
                : {comparator: colDef.comparator}),
        }
    }),
    rows: data.rows,
    sorting: data.sorting,
    filtering: data.filtering,
    colWidths: data.colWidths,
})

export const getTableDefFromSortableTableData = (
    data: SortableTableData,
    numFrozenColumns = 0,
    customValueGetter?: (params: ValueGetterParams, column: TableColumn) => any,
    disableInternalSorting = true
): TableDef => ({
    cols: data.columns.map(
        (column: TableColumn, index: number): ColDef => {
            const sortingIndex = data.sorting
                .filter((ts) =>
                    data.columns.map((c) => c.id).includes(ts.columnId)
                )
                .findIndex((entry) => entry.columnId === column.id)
            return {
                headerName: column.name,
                field: column.id,
                pinned: index < numFrozenColumns ? "left" : null,
                sort: data.sorting[sortingIndex]?.order || null,
                hide: column.hide,
                sortIndex: data.sorting[sortingIndex] ? sortingIndex : null,
                ...(data.colWidths.find((colWidth) => colWidth[column.id]) && {
                    width: (data.colWidths.find(
                        (colWidth) => colWidth[column.id]
                    ) as ColumnWidth)[column.id],
                }),
                ...(column.renderValue && {
                    cellRenderer: (params) => {
                        return column.renderValue?.(params.value)
                    },
                }),
                ...(customValueGetter && {
                    valueGetter: (params: ValueGetterParams) =>
                        customValueGetter(params, column),
                }),
                ...(disableInternalSorting
                    ? {comparator: () => 0}
                    : {comparator: column.comparator}),
            }
        }
    ),
    rows: data.rows.map((row) => row.cells),
    sorting: data.sorting,
    filtering: data.filtering,
    colWidths: data.colWidths,
})

export const getTableSortingFromEvent = (
    event: SortChangedEvent
): TableSorting[] => {
    const colState = event.columnApi.getColumnState()
    return colState
        .filter((s) => s.sort != null)
        .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
        .map((s) => ({
            columnId: s.colId as string,
            order: s.sort as "asc" | "desc",
        }))
}

export const getTableFilteringFromEvent = (
    event: FilterChangedEvent
): TableFiltering[] => {
    const filterModel = event.api.getFilterModel()
    return Object.keys(filterModel).map((colId) => ({
        columnId: colId,
        type: TableFilteringType.AG_GRID,
        value: filterModel[colId],
    }))
}

export const getFilterModelFromTableFiltering = (
    filtering: TableFiltering[]
): {[p: string]: any} => {
    return filtering.reduce(
        (model, filter) => ({
            ...model,
            [filter.columnId]: filter.value,
        }),
        {}
    )
}

export const getTableRowsFromData = (data: any[]): TableRow[] =>
    data.map(
        (entry) =>
            ({
                cells: _.mapValues(entry, (o) => ({value: o} as TableCell)),
            } as TableRow)
    )
